import { useCallback, useMemo } from 'react';
import { useAppSelector } from '../../hooks/ReduxHooks';
import isEqual from 'lodash.isequal';
import { GetAppSettings } from '../../AppSettings';
import { apiCall, safeConcatUrl } from '../../helpers/api';
import { loadingNotifications, templateCopied, updateNotifications, pdsNotificationCompleted } from '../actions';
import { useDispatch } from 'react-redux';
import { NotificationData, NotificationsDispatch } from '../notifications-types';
import { ResourceTypes } from '../data';
import { AxiosResponse } from 'axios';

export default function useNotificationData() {
  const notificationDataList = useAppSelector((state) => state.notificationCenter.notificationDataList, isEqual);
  const dispatch = useDispatch<NotificationsDispatch>();
  const appSettings = useMemo(() => GetAppSettings(), []);

  const checkAndDispatchTemplateCopiedNotification = useCallback(
    (notificationsToCheck) => {
      for (const notification of notificationsToCheck) {
        // the only notification we show for templates is the template copied notification
        if (notification.completed && notification.link && notification.link.resourceType === ResourceTypes.Template) {
          dispatch(templateCopied());
          break;
        }
      }
    },
    [dispatch]
  );

  const checkIfPdsCompleted = useCallback(
    (notificationsToCheck) => {
      for (const notification of notificationsToCheck) {
        // the only notification we show for templates is the template copied notification
        if (
          notification.completed &&
          (notification.text?.toLowerCase().includes('converted to premium') || notification.text?.toLowerCase().includes('successfully reverted'))
        ) {
          dispatch(pdsNotificationCompleted(true));
        }
      }
    },
    [dispatch]
  );

  const fetchNotifications = useCallback(async () => {
    try {
      dispatch(loadingNotifications(true));
      // not using APIM because of SignalR. Websockets are not currently setup to pass through gateway
      const notificationResult: AxiosResponse<NotificationData[]> = await apiCall(safeConcatUrl(appSettings.NotificationServiceUrl, 'notifications'));

      dispatch(updateNotifications(notificationResult.data));
      checkAndDispatchTemplateCopiedNotification(notificationResult.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      dispatch(loadingNotifications(false));
    }
  }, [appSettings.NotificationServiceUrl, checkAndDispatchTemplateCopiedNotification, dispatch]);

  return { notificationDataList, fetchNotifications, checkIfTemplateCopied: checkAndDispatchTemplateCopiedNotification, checkIfPdsCompleted };
}
